import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import logger from '../lib/logger';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string;
  fallbackPath?: string;
}

/**
 * ProtectedRoute - Skyddar routes som kräver autentisering
 * 
 * Funktioner:
 * - Oautentiserade användare omdirigeras till /login
 * - Visar loading state medan auth state laddas
 * - Kan kräva specifik roll (optional)
 * - Ingen kunddata renderas före auth är resolved
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  fallbackPath = '/login',
}) => {
  const { isAuthenticated, isLoading, role } = useAuth();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    // Kort delay för att undvika flash of loading state
    const timer = setTimeout(() => {
      setIsChecking(false);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Loading state - visa inte kunddata före auth är resolved
  if (isLoading || isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <svg className="animate-spin h-10 w-10 text-blue-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Laddar session...
          </p>
        </div>
      </div>
    );
  }

  // Kontroll: Är användaren autentiserad?
  if (!isAuthenticated) {
    logger.audit('Oautentiserad användare blockerad från skyddad route', { 
      path: location.pathname 
    });
    
    // Spara aktuell path för redirect efter login
    return (
      <Navigate 
        to={fallbackPath} 
        state={{ from: location.pathname }} 
        replace 
      />
    );
  }

  // Kontroll: Har användaren rätt roll?
  if (requiredRole) {
    const roleHierarchy: Record<string, number> = {
      admin: 4,
      technician: 3,
      agent: 2,
      viewer: 1,
    };

    const userLevel = roleHierarchy[role] || 0;
    const requiredLevel = roleHierarchy[requiredRole] || 99;

    if (userLevel < requiredLevel) {
      logger.security('Användare saknar behörighet för route', { 
        path: location.pathname,
        userRole: role,
        requiredRole,
      });
      
      // Omdirigera till /customers eller visa forbidden
      return (
        <Navigate 
          to="/unauthorized" 
          state={{ from: location.pathname }} 
          replace 
        />
      );
    }
  }

  // Användaren är autentiserad och har rätt roll
  logger.audit('Autentiserad användare tillåts till route', { 
    path: location.pathname,
    role,
  });

  // Viktigt: Ingen kunddata ska renderas här - det hanteras av OrganizationAccessGate
  return <>{children}</>;
};

export default ProtectedRoute;
